import { Box, Container, Grid } from '@material-ui/core';
import amosGanah from '@static/amos-ganah.jpeg';
import chooseFromGalleryImage from '@static/choose-from-gallery.png';
import davidNahum from '@static/david-nahum.jpeg';
import designYourOwnImage from '@static/design-your-own.png';
import heroImage from '@static/hero-serene-tombstone.png';
import obituaryIcon from '@static/obituary-icon.png';
import rabiIcon from '@static/rabi-icon.png';
import refurbishIcon from '@static/refurbish-icon.png';
import sharonaSharfi from '@static/sharona-sharfi.jpeg';
import yossiGabinson from '@static/yossi-gabinson.jpeg';
import useLanguageStore from '@stores/languageStore';
import { useTranslation } from 'react-i18next';
import ComplementaryServiceCard from '../../components/cards/ComplementaryServiceCard';
import FeatureCard from '../../components/cards/FeatureCard';
import ProductCard from '../../components/cards/ProductCard';
import ServiceCard from '../../components/cards/ServiceCard';
import TeamMemberCard from '../../components/cards/TeamMemberCard';
import SEOHead from '../../components/SEOHead';
import SectionHeader from '../../components/SectionHeader';
import HeroSection from '../../components/sections/HeroSection';
import PeaceOfMindStrip from '../../components/sections/PeaceOfMindStrip';
import { galleryCategories } from '../../data/galleryData';

const chunkArray = (array, size) => {
  const chunks = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};

const HomePage = () => {
  const { t } = useTranslation();
  const { language } = useLanguageStore();
  const isRtl = language === 'he';

  const teamMembers = [
    {
      key: 'amos',
      avatarSrc: amosGanah,
      avatarAlt: t('home.team.amos.name'),
    },
    {
      key: 'david',
      avatarSrc: davidNahum,
      avatarAlt: t('home.team.david.name'),
    },
    {
      key: 'sharona',
      avatarSrc: sharonaSharfi,
      avatarAlt: t('home.team.sharona.name'),
    },
    {
      key: 'yossi',
      avatarSrc: yossiGabinson,
      avatarAlt: t('home.team.yossi.name'),
    },
    {
      key: 'arieh',
      avatarInitial: isRtl ? 'א' : 'A',
      avatarAlt: t('home.team.arieh.name'),
    },
    {
      key: 'daniel',
      avatarInitial: isRtl ? 'ד' : 'D',
      avatarAlt: t('home.team.daniel.name'),
    },
  ];

  const features = [
    'quality',
    'materials',
    'price',
    'customization',
    'warranty',
    'timeline',
    'coordination',
  ];

  const complementaryServices = [
    {
      key: 'refurbish',
      iconSrc: refurbishIcon,
      buttonLink: '/refurbish',
      disabled: false,
    },
    {
      key: 'obituary',
      iconSrc: obituaryIcon,
      buttonLink: '/obituary',
      disabled: false,
    },
    {
      key: 'scheduleRabi',
      iconSrc: rabiIcon,
      buttonLink: '/schedule-rabbi',
      disabled: false,
    },
  ];

  const productCategoryIds = ['candle-door', 'vase', 'inner-candle-door'];
  const productCategories = productCategoryIds
    .map((id) => galleryCategories.find((cat) => cat.id === id))
    .filter((cat) => cat && cat.images.length > 0);

  return (
    <div
      className={`min-h-screen bg-stone-50 max-w-[100vw] overflow-x-hidden ${
        isRtl ? 'rtl' : 'ltr'
      }`}
    >
      <SEOHead titleKey="home.meta.title" descriptionKey="home.meta.description" />
      <HeroSection
        title={t('home.hero.title')}
        subtitle={t('home.hero.subtitle')}
        description={t('home.hero.description')}
        primaryButtonText={t('common.contactUs')}
        primaryButtonLink="/contact"
        backgroundImage={heroImage}
      />
      <PeaceOfMindStrip />

      <div className="bg-white py-12 sm:py-20">
        <Container maxWidth="lg" className="px-4 sm:px-8">
          <SectionHeader title={t('home.services.title')} className="mb-16" titleWeight="800" />
          <Box display="flex" justifyContent="center" className="mb-12">
            <Box style={{ maxWidth: '900px', margin: '0 auto' }}>
              <Grid container spacing={4}>
                <Grid item xs={12} sm={6}>
                  <ServiceCard
                    title={t('home.services.gallery.title')}
                    description={t('home.services.gallery.description')}
                    buttonText={t('common.learnMore')}
                    buttonLink="/gallery"
                    imageSrc={chooseFromGalleryImage}
                    imageAlt={t('home.services.gallery.title')}
                    isRtl={isRtl}
                    priceRange={t('home.services.gallery.priceRange')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ServiceCard
                    title={t('home.services.tombstone.title')}
                    description={t('home.services.tombstone.description')}
                    buttonText={t('common.learnMore')}
                    buttonLink="/tombstone"
                    imageSrc={designYourOwnImage}
                    imageAlt={t('home.services.tombstone.title')}
                    isRtl={isRtl}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Container>
      </div>

      <div className="bg-white py-12 sm:py-20">
        <Container maxWidth="lg" className="px-4 sm:px-8">
          <SectionHeader
            title={t('home.complementaryServices.title')}
            subtitle={t('home.complementaryServices.subtitle')}
            className="mb-16"
            titleWeight="800"
            titleColor="text-black"
            subtitleColor="text-black"
            subtitleGap="mb-6"
          />
          <Box display="flex" justifyContent="center" className="mb-12">
            <Box style={{ maxWidth: '900px', margin: '0 auto' }}>
              <Grid container spacing={4}>
                {complementaryServices.map((service) => (
                  <Grid item xs={12} sm={6} md={4} key={service.key}>
                    <ComplementaryServiceCard
                      title={t(`home.complementaryServices.${service.key}.title`)}
                      description={t(`home.complementaryServices.${service.key}.description`)}
                      buttonText={t(`home.complementaryServices.${service.key}.button`)}
                      buttonLink={service.buttonLink}
                      iconSrc={service.iconSrc}
                      iconAlt={t(`home.complementaryServices.${service.key}.title`)}
                      disabled={service.disabled}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        </Container>
      </div>

      <div className="bg-[#063447] py-12 sm:py-20 mx-3 sm:mx-6 rounded-xl">
        <Container maxWidth="lg" className="px-4 sm:px-8">
          <div className="overflow-hidden">
            <SectionHeader
              title={t('products.title')}
              subtitle={t('products.subtitle')}
              className="mb-12"
              titleWeight="800"
              titleColor="text-white"
              subtitleColor="text-white"
            />
            <Grid container spacing={4} justifyContent="center">
              {productCategories.map((category) => {
                const firstImage = category.images[0];
                if (!firstImage) return null;

                return (
                  <Grid item xs={12} sm={6} md={4} key={category.id}>
                    <ProductCard
                      title={t(`gallery.filter.${category.id}`)}
                      imageSrc={firstImage.src}
                      imageAlt={t(`gallery.filter.${category.id}`)}
                      imageCount={category.images.length}
                      linkTo={`/gallery?category=${category.id}`}
                      isRtl={isRtl}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </div>
        </Container>
      </div>

      <div className="py-12 sm:py-20">
        <Container maxWidth="lg" className="px-4 sm:px-8 !max-w-[60%]">
          <SectionHeader
            title={t('home.team.title')}
            subtitle={t('home.team.subtitle')}
            className="mb-12"
          />
          {chunkArray(teamMembers, 3).map((row) => (
            <div
              key={`team-row-${row.map((member) => member.key).join('-')}`}
              className="flex justify-center flex-wrap gap-6 mb-6 last:mb-0"
            >
              {row.map((member) => (
                <div key={member.key} className="flex justify-center">
                  <TeamMemberCard
                    name={t(`home.team.${member.key}.name`)}
                    position={t(`home.team.${member.key}.position`)}
                    description={t(`home.team.${member.key}.description`)}
                    avatarSrc={member.avatarSrc}
                    avatarInitial={member.avatarInitial}
                    avatarAlt={member.avatarAlt}
                    isRtl={isRtl}
                  />
                </div>
              ))}
            </div>
          ))}
        </Container>
      </div>

      <div className="bg-[#F8F8F8] py-8 sm:py-12">
        <Container maxWidth="lg" className="px-4 sm:px-8">
          <div className="text-center mb-8">
            <SectionHeader title={t('home.whyChoose.title')} className="mb-6" />
            <div className="mb-6 text-black leading-relaxed max-w-2xl mx-auto text-center">
              <p className="mb-4">{t('home.whyChoose.description')}</p>
              <p className="mb-8">{t('home.whyChoose.customerService')}</p>
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-6 items-stretch">
            {features.map((feature, index) => (
              <div
                key={feature}
                className="w-full sm:w-[calc(50%-1.5rem)] lg:w-[calc(25%-1.5rem)] max-w-[280px] flex"
              >
                <FeatureCard
                  title={t(`home.whyChoose.${feature}.title`)}
                  description={t(`home.whyChoose.${feature}.description`)}
                  highlight={index === 0}
                />
              </div>
            ))}
          </div>
        </Container>
      </div>
    </div>
  );
};

export default HomePage;
